import { db } from './supabase.js';

/**
 * Activity Logger Utility
 * Tracks all user interactions and logs them to Supabase
 */

// Generate or retrieve session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('jk_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('jk_session_id', sessionId);
  }
  return sessionId;
};

// Get current username from session
const getCurrentUsername = () => {
  return sessionStorage.getItem('jk_username') || 'anonymous';
};

/**
 * Log any activity
 * @param {string} actionType - Type of action (e.g., 'login', 'page_view', 'button_click')
 * @param {string} actionCategory - Category (e.g., 'auth', 'navigation', 'content')
 * @param {object} actionDetails - Additional details about the action
 */
export const logActivity = async (actionType, actionCategory, actionDetails = {}) => {
  try {
    await db.logActivity({
      username: getCurrentUsername(),
      actionType,
      actionCategory,
      actionDetails,
      pageUrl: window.location.href,
      sessionId: getSessionId(),
      userAgent: navigator.userAgent
    });
  } catch (error) {
    console.error('Failed to log activity:', error);
  }
};

// Specific activity loggers for common actions
export const ActivityLogger = {
  // Authentication
  login: (username, method = 'password') => 
    logActivity('login', 'auth', { username, method, timestamp: new Date().toISOString() }),
  
  logout: (username) => 
    logActivity('logout', 'auth', { username, timestamp: new Date().toISOString() }),
  
  vaultAccess: (username, method = 'face+totp') => 
    logActivity('vault_access', 'auth', { username, method, timestamp: new Date().toISOString() }),
  
  authFailed: (username, reason) => 
    logActivity('auth_failed', 'auth', { username, reason, timestamp: new Date().toISOString() }),

  // Navigation
  pageView: (pageName, pageUrl) => 
    logActivity('page_view', 'navigation', { pageName, pageUrl, timestamp: new Date().toISOString() }),
  
  categoryView: (categoryName) => 
    logActivity('category_view', 'navigation', { categoryName, timestamp: new Date().toISOString() }),
  
  featureAccess: (featureName, featureId) => 
    logActivity('feature_access', 'navigation', { featureName, featureId, timestamp: new Date().toISOString() }),

  // Content Creation
  diaryEntry: (entryTitle, mood = null, tags = []) => 
    logActivity('diary_entry_created', 'content', { entryTitle, mood, tags: tags.join(','), timestamp: new Date().toISOString() }),
  
  memoryAdded: (memoryTitle, propertyId) => 
    logActivity('memory_added', 'content', { memoryTitle, propertyId, timestamp: new Date().toISOString() }),
  
  timelineEvent: (eventTitle, year) => 
    logActivity('timeline_event_added', 'content', { eventTitle, year, timestamp: new Date().toISOString() }),
  
  voiceMemo: (memoName, propertyId) => 
    logActivity('voice_memo_recorded', 'content', { memoName, propertyId, timestamp: new Date().toISOString() }),

  // Content Viewing
  galleryView: (galleryName, imageCount) => 
    logActivity('gallery_viewed', 'content', { galleryName, imageCount, timestamp: new Date().toISOString() }),
  
  documentView: (documentName, documentType) => 
    logActivity('document_viewed', 'content', { documentName, documentType, timestamp: new Date().toISOString() }),
  
  timelineView: () => 
    logActivity('timeline_viewed', 'content', { timestamp: new Date().toISOString() }),

  // AI Interactions
  fozzilChat: (messagePreview, responseLength) => 
    logActivity('fozzil_chat', 'ai', { messagePreview, responseLength, timestamp: new Date().toISOString() }),
  
  aiQuery: (queryType, queryDetails) => 
    logActivity('ai_query', 'ai', { queryType, ...queryDetails, timestamp: new Date().toISOString() }),

  // Search
  search: (searchTerm, resultsCount, searchCategory) => 
    logActivity('search', 'search', { searchTerm, resultsCount, searchCategory, timestamp: new Date().toISOString() }),

  // Settings
  settingsChange: (settingName, oldValue, newValue) => 
    logActivity('settings_changed', 'settings', { settingName, oldValue, newValue, timestamp: new Date().toISOString() }),
  
  themeChange: (themeName) => 
    logActivity('theme_changed', 'settings', { themeName, timestamp: new Date().toISOString() }),

  // Errors
  error: (errorType, errorMessage, errorStack = null) => 
    logActivity('error', 'error', { errorType, errorMessage, errorStack, timestamp: new Date().toISOString() }),

  // Generic action
  custom: (actionType, actionCategory, details) => 
    logActivity(actionType, actionCategory, details)
};

export default ActivityLogger;
